/**
 * Tam çerçeve / içerik slaytları: yalnızca görünür slayttaki video oynasın.
 * Lazy (data-ed-lazy-video / data-src) kaynakları görünür olunca yükler.
 */
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Kart carousel'leri ve hero ile paylaşılan decoder bütçesi (scripts.liquid).
 * Bu script ayrı sayarsa ana sayfada iki slideshow birlikte iki 1080p videoyu
 * aynı anda decode ediyor.
 * @param {HTMLVideoElement} video
 */
function claimSlot(video) {
  const budget = window.__edVideoBudget;
  if (budget) budget.claim(video);
}

/**
 * @param {HTMLVideoElement} video
 */
function forgetSlot(video) {
  const budget = window.__edVideoBudget;
  if (budget) budget.forget(video);
}

/**
 * Bölüm ekranda mı? aria-hidden yalnızca slaytın kendi slideshow'u içindeki
 * durumunu söyler, bölümün görünür olup olmadığını söylemez — bu yüzden sayfanın
 * altındaki bir slideshow, kullanıcı oraya hiç inmeden videosunu indiriyordu.
 * @type {WeakSet<Element>}
 */
const sectionsInView = new WeakSet();
/** @type {WeakSet<Element>} */
const observedSections = new WeakSet();

const sectionObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) sectionsInView.add(entry.target);
      else sectionsInView.delete(entry.target);
      syncSlideshowVideos(entry.target);
    }
  },
  { rootMargin: '200px 0px', threshold: 0 }
);

/**
 * @param {HTMLVideoElement} video
 */
function loadAndPlay(video) {
  const perf = (typeof window !== 'undefined' && window.__edPerf) || {};
  if (perf.noVideoAutoplay) {
    video.pause();
    return;
  }

  let loaded = false;
  video.querySelectorAll('source[data-src]').forEach((source) => {
    if (!source.getAttribute('src')) {
      source.setAttribute('src', source.dataset.src);
      loaded = true;
    }
  });
  if (loaded) {
    try {
      video.load();
    } catch {
      /* ignore */
    }
  }
  video.muted = true;
  claimSlot(video);
  video.play().catch(() => {});
}

/**
 * Ekrandan çıkan videonun kaynağı hemen bırakılır. Geri kaydırmada yeniden
 * indirmeyi önlemek için bırakmayı geciktirmeyi denedim; ölçüm gürültüsü içinde
 * byte kazancı gösteremedi ama 2-7 MB'lık buffer'ı bellekte tutuyordu — yani
 * çökmenin asıl sebebine risk ekliyordu. Bu yüzden anında bırakmada kalındı.
 * @param {HTMLVideoElement} video
 */
function unloadVideo(video) {
  forgetSlot(video);
  video.pause();
  let changed = false;
  video.querySelectorAll('source[src]').forEach((source) => {
    if (!source.dataset.src) {
      source.dataset.src = source.getAttribute('src');
    }
    source.removeAttribute('src');
    changed = true;
  });
  if (changed) {
    try {
      video.load();
    } catch {
      /* ignore */
    }
  }
}

/**
 * @param {Element} slideshow
 */
function syncSlideshowVideos(slideshow) {
  if (!slideshow?.querySelectorAll) return;

  const perf = (typeof window !== 'undefined' && window.__edPerf) || {};
  const noAutoplay = !!perf.noVideoAutoplay;
  const offscreen = !sectionsInView.has(slideshow);

  slideshow.querySelectorAll('slideshow-slide').forEach((slide) => {
    const video = slide.querySelector('video.slide__video');
    if (!(video instanceof HTMLVideoElement)) return;

    const hidden = slide.getAttribute('aria-hidden') === 'true';
    if (hidden || noAutoplay || offscreen) {
      unloadVideo(video);
    } else {
      loadAndPlay(video);
    }
  });
}

/**
 * @param {Event} event
 */
function onSlideshowSelect(event) {
  const slideshow = event.target?.closest?.('slideshow-component');
  if (!slideshow || slideshow.hasAttribute('data-video-carousel')) return;
  syncSlideshowVideos(slideshow);
}

function initSlideshowVideos() {
  document.querySelectorAll('slideshow-component:not([data-video-carousel])').forEach((el) => {
    if (!observedSections.has(el)) {
      observedSections.add(el);
      sectionObserver.observe(el);
    }
    syncSlideshowVideos(el);
  });
}

document.addEventListener(SlideshowSelectEvent.eventName, onSlideshowSelect);

document.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(() => {
    initSlideshowVideos();
    requestAnimationFrame(initSlideshowVideos);
  });
});

document.addEventListener('shopify:section:load', () => {
  requestAnimationFrame(initSlideshowVideos);
});
